// Hændelseskortene på kortet: naboprotest, spidsbølge, hedebølge og datacenter-kø.
// Hver hændelse har en figur med ansigt, en advarselstrekant og en nedtælling, som man trykker på for
// at svare. Figuren popper ind, hopper når man svarer, og bølgen om byerne ånder med.

import type { BitmapText, Graphics, Sprite } from 'pixi.js';
import type { Threat, ThreatKind } from '../sim';
import { TICK_HZ } from '../sim';
import { cardOpen } from '../sim/threats';
import { bez } from './geo';
import type { P } from './geo';
import { C } from './palette';
import type { View } from './view';

const OUT = C.ink;

function bolt(g: Graphics, x: number, y: number, s: number): void {
  g.poly([x + 2 * s, y - 9 * s, x - 5 * s, y + 1 * s, x, y + 1 * s, x - 2 * s, y + 9 * s, x + 5 * s, y - 1 * s, x, y - 1 * s], true);
  g.fill(C.yellow).stroke({ width: 1.4, color: OUT, join: 'round' });
}

/** Øjne med et lille glimt, som byernes. */
function eyes(g: Graphics, x: number, y: number, dx: number, s = 1): void {
  for (const sx of [x - dx, x + dx]) {
    g.ellipse(sx, y, 1.9 * s, 2.5 * s).fill(OUT);
    g.circle(sx + 0.7 * s, y - 0.9 * s, 0.75 * s).fill(C.white);
  }
}

/** Naboprotest: en lille sur Kilowatter, der holder et skilt med en overstreget mast. */
function sign(g: Graphics): void {
  g.ellipse(-2, 0, 13, 3).fill({ color: OUT, alpha: 0.15 });
  // Skiltet på sin pind, løftet højt.
  g.roundRect(6, -30, 3, 30, 1).fill(C.wood).stroke({ width: 1.2, color: OUT });
  g.roundRect(-8, -50, 32, 21, 3).fill(C.white).stroke({ width: 2.2, color: OUT });
  g.moveTo(4, -32).lineTo(8, -47).lineTo(12, -32).moveTo(3, -42).lineTo(13, -42).moveTo(5, -37).lineTo(11, -37);
  g.stroke({ width: 1.6, color: C.petrol, cap: 'round', join: 'round' });
  g.moveTo(-3, -32).lineTo(19, -47).stroke({ width: 3, color: C.orange, cap: 'round' });
  // Kilowatteren: rund, varm og lidt fornærmet.
  g.ellipse(-6, -10, 9, 10).fill(C.wallWarm).stroke({ width: 2, color: OUT });
  g.moveTo(1, -12).lineTo(7, -16).stroke({ width: 2, color: OUT, cap: 'round' });
  eyes(g, -6, -11, 3.4, 0.9);
  g.moveTo(-11, -16).lineTo(-7.5, -14.6).moveTo(-1, -16).lineTo(-4.5, -14.6).stroke({ width: 1.4, color: OUT, cap: 'round' });
  g.moveTo(-9, -5).quadraticCurveTo(-6, -7.5, -3, -5).stroke({ width: 1.5, color: OUT, cap: 'round' });
}

/** Spidsbølge: en elbil med et lyn over taget. */
function carSpike(g: Graphics): void {
  g.ellipse(0, 0, 18, 3).fill({ color: OUT, alpha: 0.15 });
  g.poly([-10, -12, -5, -19, 7, -19, 12, -12], true).fill(C.white).stroke({ width: 1.8, color: OUT, join: 'round' });
  g.roundRect(-18, -13, 36, 10, 4).fill(C.turquoise).stroke({ width: 2, color: OUT });
  // Forruden er øjnene: bilen glæder sig til at lade.
  eyes(g, 1, -15.5, 3.6, 0.8);
  g.moveTo(-13, -8).quadraticCurveTo(-9, -5.5, -5, -8).stroke({ width: 1.4, color: OUT, cap: 'round' });
  g.circle(-10, -3, 4).fill(OUT);
  g.circle(10, -3, 4).fill(OUT);
  g.circle(-10, -3, 1.6).fill(C.white);
  g.circle(10, -3, 1.6).fill(C.white);
  bolt(g, 0, -29, 1);
}

/** Hedebølge: en varm sol med stråler. */
function sun(g: Graphics): void {
  for (let i = 0; i < 10; i++) {
    const a = (i / 10) * Math.PI * 2;
    g.moveTo(Math.cos(a) * 15, -18 + Math.sin(a) * 15).lineTo(Math.cos(a) * 21, -18 + Math.sin(a) * 21);
  }
  g.stroke({ width: 3, color: C.orange, cap: 'round' });
  g.circle(0, -18, 12).fill(C.yellow).stroke({ width: 2, color: OUT });
  g.circle(-4, -20, 1.6).fill(OUT);
  g.circle(4, -20, 1.6).fill(OUT);
  g.moveTo(-4, -15).quadraticCurveTo(0, -12, 4, -15).stroke({ width: 1.6, color: OUT, cap: 'round' });
}

/** Datacenter-kø: tre serverskabe på række, det bageste stiplet som et muligt fantom. */
function queue(g: Graphics): void {
  g.ellipse(0, 0, 20, 3).fill({ color: OUT, alpha: 0.15 });
  g.roundRect(4, -26, 14, 24, 2).stroke({ width: 1.6, color: C.petrol, alpha: 0.6 });
  g.roundRect(-18, -30, 14, 28, 2).fill(C.petrol).stroke({ width: 1.8, color: OUT });
  g.roundRect(-6, -34, 14, 32, 2).fill(C.petrol).stroke({ width: 1.8, color: OUT });
  for (let r = 0; r < 3; r++) g.rect(-15, -26 + r * 8, 8, 2).fill(C.turquoise);
  // Det forreste skab har LED-øjne og et lille smil: robotten vil gerne have strøm.
  g.roundRect(-3.5, -29, 3, 4, 1).fill(C.yellow);
  g.roundRect(2.5, -29, 3, 4, 1).fill(C.yellow);
  g.moveTo(-2, -21).quadraticCurveTo(1, -18.5, 4, -21).stroke({ width: 1.4, color: C.turquoise, cap: 'round' });
  g.rect(-3, -13, 8, 2).fill(C.turquoise);
  g.rect(-3, -8, 8, 2).fill(C.turquoise);
  // Fantomet bagest: et spøgelsesansigt i stiplerne.
  g.circle(9, -16, 1.4).fill({ color: C.petrol, alpha: 0.6 });
  g.circle(13, -16, 1.4).fill({ color: C.petrol, alpha: 0.6 });
  g.circle(11, -11, 1.8).stroke({ width: 1.3, color: C.petrol, alpha: 0.6 });
}

/** Teksturerne til atlasset (navn og tegning). */
export const M2_DRAWS: Record<string, (g: Graphics) => void> = { sign, carSpike, sun, queue };

const TEX: Partial<Record<ThreatKind, string>> = { protest: 'sign', spike: 'carSpike', heatwave: 'sun', dc_rush: 'queue' };

/** Teksturen til en figur fra dette lag, eller null hvis den tegnes et andet sted. */
export function m2Texture(kind: ThreatKind): string | null {
  return TEX[kind] ?? null;
}

/** Det af trusselsvisningen, dette lag bruger. */
export interface M2Parts {
  body: Sprite;
  marker: Sprite;
  count: BitmapText;
  at: P;
  /** Spor: telefonkort: forskydningen væk fra huse, navne og andre figurer (declutter.ts). */
  nudge?: P;
  /** Hvornår spilleren svarede (ms), til hoppet. */
  popAt: number;
}

/** Elastisk overshoot fra 0 til 1: figuren popper ind. */
function outBack(p: number): number {
  const c = 1.9;
  const q = p - 1;
  return 1 + (c + 1) * q * q * q + c * q * q;
}

function centroid(ids: readonly string[] | undefined, v: View): P | null {
  let x = 0;
  let y = 0;
  let n = 0;
  for (const id of ids ?? []) {
    const p = v.pos.get(id);
    if (!p) continue;
    x += p.x;
    y += p.y;
    n++;
  }
  return n > 0 ? { x: x / n, y: y / n } : null;
}

/** Hvor figuren står: ved ledningen, ved byerne eller øverst over kortet. */
function anchorOf(t: Threat, v: View): P | null {
  const k = v.k;
  if (t.kind === 'protest') {
    const geo = v.lines.get(t.targetLine);
    if (geo) {
      const m = bez(geo.a, geo.c, geo.b, 0.5);
      return { x: m.x, y: m.y - 10 * k };
    }
    const c = centroid(t.nodes, v);
    return c ? { x: c.x, y: c.y - 30 * k } : null;
  }
  if (t.kind === 'heatwave') {
    // Solen står over midten af de luftledninger, der bliver varme.
    let x = 0;
    let y = 0;
    let n = 0;
    for (const id of t.atRisk) {
      const geo = v.lines.get(id);
      if (!geo) continue;
      const m = bez(geo.a, geo.c, geo.b, 0.5);
      x += m.x;
      y += m.y;
      n++;
    }
    if (n === 0) {
      const top = v.cam.toScreen({ x: v.bounds.x + v.bounds.w / 2, y: v.bounds.y + v.bounds.h / 2 });
      return { x: top.x, y: top.y };
    }
    return { x: Math.max(40 * k, Math.min(v.width - 40 * k, x / n + 30 * k)), y: y / n - 30 * k };
  }
  const c = centroid(t.nodes, v);
  return c ? { x: c.x, y: c.y - 44 * k } : null;
}

/** Tegn én hændelse fra et kort. Båndet (g) ligger under noderne. */
export function drawCardThreat(t: Threat, tv: M2Parts, v: View, g: Graphics): void {
  const k = v.k;
  const clock = v.clock;
  const a0 = anchorOf(t, v);
  const p = a0 && tv.nudge ? { x: a0.x + tv.nudge.x, y: a0.y + tv.nudge.y } : a0;
  if (!p) {
    tv.body.visible = tv.marker.visible = tv.count.visible = false;
    return;
  }
  const warn = t.phase === 'warning';
  const pulse = v.calm ? 0.5 : 0.5 + 0.5 * Math.sin(v.now / 240);
  // Ind under varslet, ud de sidste 2 s (eller når kortet er afgjort).
  const fin = Math.max(0, Math.min(1, (t.endAt - clock) / 20));
  const fade = warn ? Math.min(1, (clock - t.warnAt) / 8) : fin;

  if (t.kind === 'spike') {
    // Bølgen: orange ringe om byerne, der får mere forbrug.
    for (const id of t.nodes ?? []) {
      const q = v.pos.get(id);
      if (!q) continue;
      const r = (warn ? 30 : 34 + pulse * 6) * k;
      g.circle(q.x, q.y, r).fill({ color: C.orange, alpha: (warn ? 0.08 : 0.16) * fade }).stroke({ width: 2.5 * k, color: C.orange, alpha: (0.5 + pulse * 0.3) * fade });
    }
  } else if (t.kind === 'heatwave') {
    // Varme luftledninger: et gyldent skær langs dem, der kan bære mindre.
    for (const id of t.atRisk) {
      const geo = v.lines.get(id);
      if (!geo) continue;
      g.moveTo(geo.a.x, geo.a.y).quadraticCurveTo(geo.c.x, geo.c.y, geo.b.x, geo.b.y);
    }
    g.stroke({ width: 9 * k, color: C.yellow, alpha: (warn ? 0.18 : 0.3 + pulse * 0.12) * fade, cap: 'round' });
  } else if (t.kind === 'dc_rush') {
    // De nye kunder: stiplede ringe, hvor de dukker op.
    for (const id of t.nodes ?? []) {
      const q = v.pos.get(id);
      if (!q) continue;
      const r = 24 * k;
      for (let i = 0; i < 12; i += 2) {
        const a0 = (i / 12) * Math.PI * 2 + (v.calm ? 0 : v.now / 1200);
        g.arc(q.x, q.y, r, a0, a0 + Math.PI / 6).stroke({ width: 2.5 * k, color: C.turquoise, alpha: 0.8 * fade });
      }
    }
  } else if (t.kind === 'protest' && !warn && !t.shooed) {
    // Borgermødet: ledningen kører halvt. En rolig petrol ring om skiltet.
    g.circle(p.x, p.y - 20 * k, 30 * k).stroke({ width: 2 * k, color: C.petrol, alpha: 0.5 * fade });
  }

  const bob = v.calm ? 0 : Math.sin(v.now / 300 + t.warnAt) * 2 * k;
  tv.body.visible = true;
  tv.body.alpha = fade;
  tv.body.position.set(p.x, p.y + bob);
  // Pop ind de første 0,5 s, og et squash-hop når der svares (sprite-fod i bunden).
  const pop = v.calm ? 1 : outBack(Math.max(0, Math.min(1, (clock - t.warnAt) / 5)));
  const since = v.now - tv.popAt;
  const sq = !v.calm && since >= 0 && since < 420 ? Math.sin((since / 420) * Math.PI * 2) * 0.22 * (1 - since / 420) : 0;
  tv.body.scale.set(k * 1.1 * pop * (1 + sq), k * 1.1 * pop * (1 - sq));
  tv.at = { x: p.x, y: p.y - 18 * k };

  const open = cardOpen(t);
  tv.marker.visible = warn;
  tv.count.visible = warn;
  if (open) {
    // Tryk-ring: "tryk her og svar".
    const ph = v.calm ? 0.4 : (v.now / 900) % 1;
    g.circle(tv.at.x, tv.at.y, (30 + ph * 14) * k).stroke({ width: 3 * k, color: C.orange, alpha: 0.8 * (1 - ph) });
    g.circle(tv.at.x, tv.at.y, 30 * k).fill({ color: C.white, alpha: 0.35 }).stroke({ width: 2.5 * k, color: C.orange });
  }
  if (warn) {
    // Trekant og nedtælling side om side over figuren, så de ikke dækker bynavne under den.
    const secs = Math.max(0, Math.ceil((t.hitAt - clock) / TICK_HZ));
    const my = Math.max(20 * k, p.y - 58 * k);
    tv.marker.position.set(p.x - 14 * k, my);
    tv.marker.scale.set(k * 0.7);
    tv.count.text = `${secs}s`;
    tv.count.position.set(p.x + 16 * k, my + 2 * k);
    tv.count.scale.set(k * 0.75);
    tv.marker.alpha = tv.count.alpha = open ? 1 : 0.6;
  }
}
